"use client";

import { useMemo } from "react";
import { resolveObjectArray, resolveStringArray, resolveUnknown } from "./binding";
import type { RecordAny } from "./types";

export const MAX_ROLL_PICKER_COLUMN_COUNT = 3;
export const ROLL_PICKER_HEIGHT = 122;
export const ROLL_PICKER_FRAME_BORDER = "#FF596C";
export const ROLL_PICKER_FRAME_EDGE = "#FFEAE3";

export type RollPickerColumn = { title: string; items: string[] };
export type RollPickerSnapshot = { selectedValues: string[]; selectedIndexes: number[]; hasCustomSelection: boolean };

function isRecord(value: unknown): value is RecordAny {
  return Boolean(value && typeof value === "object");
}

export function resolveInlineObjectArray(expr: unknown, data: RecordAny): RecordAny[] {
  if (Array.isArray(expr)) return expr.filter(isRecord);
  return resolveObjectArray(expr, data);
}

export function resolveInlineStringArray(expr: unknown, data: RecordAny): string[] {
  if (Array.isArray(expr)) return expr.filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  return resolveStringArray(expr, data);
}

export function resolveRollPickerRawSelection(expr: unknown, data: RecordAny): unknown[] {
  const value = resolveUnknown(expr, data);
  return Array.isArray(value) ? value : [];
}

export function parseRollPickerColumns(expr: unknown, data: RecordAny): RollPickerColumn[] {
  return resolveInlineObjectArray(expr, data)
    .slice(0, MAX_ROLL_PICKER_COLUMN_COUNT)
    .map((entry) => {
      const title = typeof entry.title === "string" && entry.title.trim().length > 0 ? entry.title : "";
      const items = resolveInlineStringArray(entry.items, data);
      if (!title || items.length === 0) return null;
      return { title, items };
    })
    .filter((entry): entry is RollPickerColumn => Boolean(entry));
}

export function displayRollPickerValues(column: RollPickerColumn): string[] {
  return [column.title, ...column.items];
}

export function clampWrappedIndex(index: number, length: number): number {
  return ((index % length) + length) % length;
}

export function arraysEqual<T>(left: T[], right: T[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

export function resolveRollPickerDisplayIndexes(columns: RollPickerColumn[], selection: unknown[]): number[] {
  return columns.map((column, index) => {
    const values = displayRollPickerValues(column);
    const current = selection[index];
    if (typeof current !== "string") return 0;
    const matchIndex = values.indexOf(current);
    return matchIndex >= 0 ? matchIndex : 0;
  });
}

export function buildRollPickerSnapshot(columns: RollPickerColumn[], indexes: number[]): RollPickerSnapshot {
  const selectedValues = columns.map((column, index) => {
    const values = displayRollPickerValues(column);
    return values[clampWrappedIndex(indexes[index] ?? 0, values.length)] ?? column.title;
  });
  const selectedIndexes = columns.map((column, index) => clampWrappedIndex(indexes[index] ?? 0, displayRollPickerValues(column).length) - 1);
  return { selectedValues, selectedIndexes, hasCustomSelection: selectedIndexes.some((value) => value >= 0) };
}

function previewValue(values: string[], selectedIndex: number, offset: number): string {
  return values[clampWrappedIndex(selectedIndex + offset, values.length)] ?? "";
}

export function RollPickerColumnView({ column, selectedIndex, onChange }: { column: RollPickerColumn; selectedIndex: number; onChange: (nextIndex: number) => void }) {
  const values = useMemo(() => displayRollPickerValues(column), [column]);
  const currentIndex = clampWrappedIndex(selectedIndex, values.length);
  const step = (delta: number) => onChange(clampWrappedIndex(currentIndex + delta, values.length));

  return (
    <div
      className="relative flex min-w-0 flex-1 items-center justify-center overflow-hidden px-2 text-center outline-none"
      tabIndex={0}
      onWheel={(event) => {
        event.preventDefault();
        step(event.deltaY > 0 ? 1 : -1);
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          step(-1);
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          step(1);
        }
      }}
    >
      <button type="button" aria-label={`Choose previous ${column.title}`} className="absolute inset-x-0 top-0 z-20 h-1/2 cursor-n-resize" onClick={() => step(-1)} />
      <button type="button" aria-label={`Choose next ${column.title}`} className="absolute inset-x-0 bottom-0 z-20 h-1/2 cursor-s-resize" onClick={() => step(1)} />
      <div className="pointer-events-none absolute inset-x-2 top-1/2 h-9 -translate-y-1/2 rounded-[10px] bg-white/88 shadow-[inset_0_1px_0_rgba(255,255,255,0.92),0_2px_10px_rgba(22,22,21,0.06)]" />
      <div className="relative z-10 flex w-full flex-col items-center">
        <span className="w-full truncate text-[13px] font-semibold leading-8 text-[#161615]/14">{previewValue(values, currentIndex, -1)}</span>
        <span className="w-full truncate text-base font-semibold leading-9 text-[#161615]">{previewValue(values, currentIndex, 0)}</span>
        <span className="w-full truncate text-[13px] font-semibold leading-8 text-[#161615]/14">{previewValue(values, currentIndex, 1)}</span>
      </div>
    </div>
  );
}

export function RollPickerFrame({ columns, indexes, onChange }: { columns: RollPickerColumn[]; indexes: number[]; onChange: (columnIndex: number, nextIndex: number) => void }) {
  return (
    <div className="flex h-[122px] items-center">
      <div className="flex w-[6px] justify-end">
        <div className="h-[44px] w-[6px] rounded-l-[8px] bg-[#FF596C]" />
      </div>
      <div
        className="flex min-w-0 flex-1 overflow-hidden rounded-[12px] border-2 px-0 py-3"
        style={{ height: ROLL_PICKER_HEIGHT, borderColor: ROLL_PICKER_FRAME_BORDER, background: `linear-gradient(180deg, ${ROLL_PICKER_FRAME_EDGE} 0%, #FFFFFF 34%, #FFFFFF 66%, ${ROLL_PICKER_FRAME_EDGE} 100%)` }}
      >
        {columns.map((column, index) => (
          <div key={`${column.title}-${index}`} className={`flex min-w-0 flex-1 ${index < columns.length - 1 ? "border-r-2" : ""}`} style={{ borderColor: ROLL_PICKER_FRAME_BORDER }}>
            <RollPickerColumnView column={column} selectedIndex={indexes[index] ?? 0} onChange={(nextIndex) => onChange(index, nextIndex)} />
          </div>
        ))}
      </div>
      <div className="flex w-[6px] justify-start">
        <div className="h-[44px] w-[6px] rounded-r-[8px] bg-[#FF596C]" />
      </div>
    </div>
  );
}
